import THREE from "three";

/**
 * Unlit material for 3D line geometry.
 *
 * Use with geometry whose index buffer contains consecutive vertex-index pairs (each pair defines one line segment).
 * Lines are always 1 px wide.
 *
 * @alias Autodesk.Viewing.Scene.LineMaterial
 * @memberOf Autodesk.Viewing.Scene
 * @class
 */
export class LineMaterial extends THREE.LineBasicMaterial {
  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial
   * @param {object}                        [params]
   * @param {Autodesk.Viewing.Scene.Color}  [params.color]        Line color. Default: white.
   * @param {number}                        [params.opacity]      Line opacity in `[0, 1]`. Default: `1`.
   * @param {boolean}                       [params.vertexColors] Use per-vertex colors stored in the geometry. Default: `false`.
   * @param {boolean}                       [params.depthWrite]   Write to the depth buffer. Default: `true`.
   * @param {boolean}                       [params.depthTest]    Depth-test against the depth buffer. Default: `true`.
   * @param {boolean}                       [params.transparent]  Render in the transparent pass with alpha blending. Default: `false`.
   */
  constructor(params = {}) {
    super(params);
    // LineBasicMaterial does not support environment maps; prevent MaterialManager.addHDRMaterial
    // from assigning the scene reflection map, which would corrupt the rendered line color.
    this.disableEnvMap = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getColor
   * @returns {Autodesk.Viewing.Scene.Color} A copy of the line color.
   */
  getColor() {return this.color.clone();}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setColor
   * @param {Autodesk.Viewing.Scene.Color} c Line color. Copied into the existing Color.
   */
  setColor(c) {this.color.copy(c);this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getOpacity
   * @returns {number} Line opacity in `[0, 1]`.
   */
  getOpacity() {return this.opacity;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setOpacity
   * @param {number} o Line opacity in `[0, 1]`. Requires `setTransparent(true)` for `< 1`.
   */
  setOpacity(o) {this.opacity = o;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getVertexColors
   * @returns {boolean} Whether to use per-vertex colors from the geometry's `color` attribute.
   */
  getVertexColors() {return this.vertexColors;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setVertexColors
   * @param {boolean} v Whether to use per-vertex colors.
   */
  setVertexColors(v) {this.vertexColors = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getDepthWrite
   * @returns {boolean} Whether to write to the depth buffer.
   */
  getDepthWrite() {return this.depthWrite;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setDepthWrite
   * @param {boolean} v Whether to write to the depth buffer.
   */
  setDepthWrite(v) {this.depthWrite = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getDepthTest
   * @returns {boolean} Whether to depth-test against the depth buffer.
   */
  getDepthTest() {return this.depthTest;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setDepthTest
   * @param {boolean} v Whether to depth-test against the depth buffer.
   */
  setDepthTest(v) {this.depthTest = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#getTransparent
   * @returns {boolean} Whether the material renders in the transparent pass.
   */
  getTransparent() {return this.transparent;}

  /**
   * @alias Autodesk.Viewing.Scene.LineMaterial#setTransparent
   * @param {boolean} v Whether the material renders in the transparent pass with alpha blending.
   */
  setTransparent(v) {this.transparent = v;this.needsUpdate = true;}
}